import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export type ArchiveItem =
  | {
      kind: "idea";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      type: "concept" | "project";
      date: string;
      vote_count: number;
      href: string;
    }
  | {
      kind: "curated";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      source_org: string;
      source_url: string;
      date: string;
      href: string;
    };

const ArchiveCard = ({ item }: { item: ArchiveItem }) => {
  return (
    <article className="break-inside-avoid mb-4 group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-colors">
      {item.cover && (
        <Link to={item.href} className="block overflow-hidden">
          <img
            src={item.cover}
            alt={item.title}
            loading="lazy"
            className="w-full max-h-72 object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        </Link>
      )}
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {item.kind === "curated" ? (
            <Badge className="text-xs bg-accent/15 text-accent-foreground border border-accent/30">
              EM Curated
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">Community</Badge>
          )}
          {item.kind === "idea" && (
            <Badge variant="outline" className="text-xs capitalize">{item.type}</Badge>
          )}
          {item.category && (
            <Badge variant="outline" className="text-xs">{item.category}</Badge>
          )}
        </div>

        <Link
          to={item.href}
          className="block font-display font-semibold text-foreground leading-snug mb-1.5 hover:text-primary transition-colors"
        >
          {item.title}
        </Link>

        {item.summary && (
          <p className="text-sm text-muted-foreground line-clamp-3">{item.summary}</p>
        )}

        <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
          {item.kind === "curated" ? (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 truncate hover:text-primary"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate">Originally from {item.source_org}</span>
            </a>
          ) : (
            <span>▲ {item.vote_count}</span>
          )}
          <span className="shrink-0">
            {formatDistanceToNow(new Date(item.date), { addSuffix: true })}
          </span>
        </div>
      </div>
    </article>
  );
};

export default ArchiveCard;